import type { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

const requiredNullSelection = z.literal(null).refine(() => false, { message: 'Required' });
const requiredTriStateBoolean = z
  .boolean()
  .nullable()
  .refine((value) => value !== null, {
    message: 'Required',
  });

const GkgYesMeasureEnumValues = ['NONE', 'INITIATED', 'APPROVED'] as const;
const GkgNoMeasureEnumValues = ['CLARIFIED_WITH_GKG_OFFICER', 'NO_EXPORT', 'DELIVERY_TO_ORIGINAL_SUPPLIER'] as const;

export const GkgYesMeasureValues = [...GkgYesMeasureEnumValues];
export const GkgNoMeasureValues = [...GkgNoMeasureEnumValues];

const gkgSubmitSchema = z
  .discriminatedUnion('required', [
    z.object({
      required: z.literal(false),
      measures: z.enum(GkgNoMeasureEnumValues).default('CLARIFIED_WITH_GKG_OFFICER'),
      comment: z.string().default(''),
    }),
    z.object({
      required: z.literal(true),
      measures: z.enum(GkgYesMeasureEnumValues).default('NONE'),
      comment: z.string().default(''),
    }),
  ])
  .default({ required: false, comment: '', measures: 'CLARIFIED_WITH_GKG_OFFICER' });

const gkgFormSchema = z.discriminatedUnion('required', [
  z.object({
    required: requiredNullSelection,
    measures: z.null().optional(),
    comment: z.string().default(''),
  }),
  z.object({
    required: z.literal(false),
    measures: z.enum(GkgNoMeasureEnumValues).or(requiredNullSelection),
    comment: z.string().default(''),
  }),
  z.object({
    required: z.literal(true),
    measures: z.enum(GkgYesMeasureEnumValues).or(requiredNullSelection),
    comment: z.string().default(''),
  }),
]);

const NdaAvailabilityEnumValues = ['AVAILABLE', 'REQUESTED'] as const;
export const NdaAvailabilityValues = [...NdaAvailabilityEnumValues];

const ndaSubmitSchema = z.discriminatedUnion('required', [
  z.object({
    required: z.literal(false),
  }),
  z.object({
    required: z.literal(true),
    availability: z.enum(NdaAvailabilityEnumValues),
  }),
]);

const ndaFormSchema = z.discriminatedUnion('required', [
  z.object({
    required: requiredNullSelection,
    availability: z.null().optional(),
  }),
  z.object({
    required: z.literal(false),
  }),
  z.object({
    required: z.literal(true),
    availability: z.enum(NdaAvailabilityEnumValues).or(requiredNullSelection),
  }),
]);

const KkbClarificationEnumValues = ['NONE', 'INITIATED', 'APPROVED'] as const;
export const KkbClarificationValues = [...KkbClarificationEnumValues];

const kkbSubmitSchema = z
  .union([
    z.object({
      applicable: z.literal(false),
      hazardous: z.boolean(),
    }),
    z.object({
      applicable: z.literal(true),
      controlledZone: z.boolean(),
      tenuFourZone: z.boolean(),
      hazardous: z.literal(false),
      clarification: z.null().optional().default(null),
      comment: z.string().default(''),
    }),
    z.object({
      applicable: z.literal(true),
      controlledZone: z.boolean(),
      tenuFourZone: z.boolean(),
      hazardous: z.literal(true),
      clarification: z.enum(KkbClarificationEnumValues),
      comment: z.string().default(''),
    }),
  ])
  .default({ applicable: false, hazardous: false });

const kkbFormSchema = z.union([
  z.object({
    applicable: requiredNullSelection,
    controlledZone: z.null().optional(),
    tenuFourZone: z.null().optional(),
    hazardous: z.null().optional(),
    clarification: z.null().optional(),
    comment: z.string().default(''),
  }),
  z.object({
    applicable: z.literal(false),
    hazardous: z.boolean().default(false),
  }),
  z.object({
    applicable: z.literal(true),
    controlledZone: requiredTriStateBoolean,
    tenuFourZone: requiredTriStateBoolean,
    hazardous: requiredNullSelection,
    clarification: z.null().optional(),
    comment: z.string().default(''),
  }),
  z.object({
    applicable: z.literal(true),
    controlledZone: requiredTriStateBoolean,
    tenuFourZone: requiredTriStateBoolean,
    hazardous: z.literal(false),
    clarification: z.null().optional(),
    comment: z.string().default(''),
  }),
  z.object({
    applicable: z.literal(true),
    controlledZone: requiredTriStateBoolean,
    tenuFourZone: requiredTriStateBoolean,
    hazardous: z.literal(true),
    clarification: z.enum(KkbClarificationEnumValues).or(requiredNullSelection),
    comment: z.string().default(''),
  }),
]);

const OrderTypeEnumValues = ['ZDL'] as const;
const QGroupEnumValues = ['A', 'B', 'C', 'D'] as const;

export const OrderTypeValues = [...OrderTypeEnumValues];
export const QGroupValues = [...QGroupEnumValues];

export const intakeNuclearComplianceFormStepFormSchema = z.object({
  orderType: z.enum(OrderTypeEnumValues).default('ZDL'),
  qGroup: z.enum(QGroupEnumValues).default('D'),

  validationByProcurement: requiredTriStateBoolean,

  gkg: gkgFormSchema,
  nda: ndaFormSchema,
  kkb: kkbFormSchema,
});

export const intakeNuclearComplianceFormStepSubmitSchema = z.object({
  orderType: z.enum(OrderTypeEnumValues).default('ZDL'),
  qGroup: z.enum(QGroupEnumValues).default('D'),

  validationByProcurement: z.boolean(),

  gkg: gkgSubmitSchema,
  nda: ndaSubmitSchema,
  kkb: kkbSubmitSchema,
});

export type IntakeNuclearComplianceFormStepInput = z.input<typeof intakeNuclearComplianceFormStepFormSchema>;
export type IntakeNuclearComplianceFormStepData = z.output<typeof intakeNuclearComplianceFormStepSubmitSchema>;

export const IntakeNuclearComplianceFormStepDefaultValues: DefaultValues<IntakeNuclearComplianceFormStepInput> = {
  qGroup: 'D',
  orderType: 'ZDL',
  validationByProcurement: null,
  gkg: { required: null, comment: '', measures: null },
  nda: { required: null },
  kkb: {
    applicable: null,
    hazardous: null,
    controlledZone: null,
    tenuFourZone: null,
    clarification: null,
    comment: '',
  },
};
